import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Building2, Save, CreditCard, Plus, Pencil, Trash2, Upload, X, User as UserIcon, Briefcase, Stamp, AlignLeft } from "lucide-react";

async function fileToB64(file) {
  return new Promise((r) => { const f=new FileReader(); f.onload=()=>r(f.result); f.readAsDataURL(file); });
}

export default function CompanySettingsPage() {
  const [s, setS] = useState(null);
  const [banks, setBanks] = useState([]);
  const [bankEdit, setBankEdit] = useState(null);
  const ttdRef = useRef(null);
  const kopRef = useRef(null);
  const stampRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([api.get("/settings/company"), api.get("/banks")]);
      setS(a.data); setBanks(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function saveCompany() {
    try { const { data } = await api.put("/settings/company", s); setS(data); toast.success("Tersimpan"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function onTtd(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToB64(f); setS({ ...s, signature_image: b64 });
  }
  async function onKop(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToB64(f); setS({ ...s, kop_image: b64 });
  }
  async function onStamp(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToB64(f); setS({ ...s, stamp_image: b64 });
  }
  async function saveBank() {
    const body = { nama_bank: bankEdit.nama_bank, nomor: bankEdit.nomor, atas_nama: bankEdit.atas_nama };
    try {
      if (bankEdit.id) await api.put(`/banks/${bankEdit.id}`, body);
      else await api.post("/banks", body);
      toast.success("Rekening disimpan"); setBankEdit(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function delBank(id) {
    try { await api.delete(`/banks/${id}`); toast.success("Rekening dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  if (!s) return <div className="text-sm text-ink-500">Memuat…</div>;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="company-settings-page">
      <PageHeader overline="Administrasi" title="Pengaturan Perusahaan" description="Data perusahaan, tanda tangan direktur, kop surat, dan rekening pembayaran yang muncul di PDF." icon={Building2} tone="brand" />

      <Card className="p-6 border-border shadow-none space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label><Briefcase className="inline h-3 w-3" /> Nama Perusahaan</Label><Input value={s.nama_perusahaan||""} onChange={e=>setS({...s,nama_perusahaan:e.target.value})} data-testid="inp-nama-perusahaan" /></div>
          <div><Label><UserIcon className="inline h-3 w-3" /> Nama Direktur</Label><Input value={s.nama_direktur||""} onChange={e=>setS({...s,nama_direktur:e.target.value})} data-testid="inp-direktur" /></div>
          <div className="sm:col-span-2"><Label>Jabatan</Label><Input value={s.jabatan_direktur||""} onChange={e=>setS({...s,jabatan_direktur:e.target.value})} data-testid="inp-jabatan" /></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-md border border-dashed border-border p-3">
            <Label>Tanda Tangan Direktur</Label>
            <input ref={ttdRef} type="file" accept="image/*" className="hidden" onChange={onTtd} data-testid="upload-ttd" />
            {s.signature_image ? <img src={s.signature_image} alt="ttd" className="mt-2 h-20 object-contain bg-white" /> : <div className="mt-2 text-xs text-ink-500">Belum ada tanda tangan.</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={()=>ttdRef.current?.click()} data-testid="btn-pick-ttd"><Upload className="mr-1 h-3 w-3" /> Pilih file</Button>
              {s.signature_image && (
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={()=>{ if (ttdRef.current) ttdRef.current.value=""; setS({...s, signature_image: null}); }} data-testid="btn-clear-ttd"><X className="mr-1 h-3 w-3" /> Hapus</Button>
              )}
            </div>
            <p className="mt-1 text-[10px] text-ink-500">Hapus jika ingin TTD basah/manual di hasil cetak.</p>
          </div>
          <div className="rounded-md border border-dashed border-border p-3">
            <Label><Stamp className="inline h-3 w-3" /> Cap / Stempel Perusahaan</Label>
            <input ref={stampRef} type="file" accept="image/*" className="hidden" onChange={onStamp} data-testid="upload-stamp" />
            {s.stamp_image ? <img src={s.stamp_image} alt="stamp" className="mt-2 h-20 object-contain bg-white" /> : <div className="mt-2 text-xs text-ink-500">Belum ada stempel.</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={()=>stampRef.current?.click()} data-testid="btn-pick-stamp"><Upload className="mr-1 h-3 w-3" /> Pilih file</Button>
              {s.stamp_image && (
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={()=>{ if (stampRef.current) stampRef.current.value=""; setS({...s, stamp_image: null}); }} data-testid="btn-clear-stamp"><X className="mr-1 h-3 w-3" /> Hapus</Button>
              )}
            </div>
            <p className="mt-1 text-[10px] text-ink-500">Hapus jika ingin stempel asli/basah di hasil cetak.</p>
          </div>
          <div className="rounded-md border border-dashed border-border p-3">
            <Label>Kop Surat (override default)</Label>
            <input ref={kopRef} type="file" accept="image/*" className="hidden" onChange={onKop} data-testid="upload-kop" />
            {s.kop_image ? <img src={s.kop_image} alt="kop" className="mt-2 h-20 object-contain bg-white" /> : <div className="mt-2 text-xs text-ink-500">Memakai kop default sistem.</div>}
            <Button variant="outline" size="sm" className="mt-2" onClick={()=>kopRef.current?.click()}><Upload className="mr-1 h-3 w-3" /> Pilih file</Button>
            <p className="mt-1 text-[10px] text-ink-500">Akan dirender sebagai latar full A4 di setiap halaman.</p>
          </div>
        </div>

        <div>
          <Label><AlignLeft className="inline h-3 w-3" /> Paragraf Perkenalan (hanya muncul di PDF SPH)</Label>
          <Textarea rows={4} value={s.intro_paragraph || ""} onChange={e=>setS({...s, intro_paragraph: e.target.value})} placeholder="Hallo sobat HPM, kami PT. ..." data-testid="inp-intro" />
          <p className="mt-1 text-xs text-ink-500">Tampil setelah "Dengan Hormat," dan sebelum tabel rincian.</p>
        </div>
        <div className="flex justify-end"><Button onClick={saveCompany} className="bg-brand text-white" data-testid="save-company"><Save className="mr-1 h-4 w-4" /> Simpan</Button></div>
      </Card>

      <Card className="p-6 border-border shadow-none">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><div className="grid h-9 w-9 place-items-center rounded-md bg-amber-500 text-white"><CreditCard className="h-4 w-4" /></div><div><p className="overline text-amber-700">Pembayaran</p><h2 className="font-heading text-lg font-bold">Rekening Bank</h2></div></div>
          <Button onClick={()=>setBankEdit({nama_bank:"",nomor:"",atas_nama:""})} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="btn-add-bank"><Plus className="mr-1 h-4 w-4" /> Tambah Rekening</Button>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Bank</TableHead><TableHead>Nomor</TableHead><TableHead>Atas Nama</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {banks.length===0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-ink-500">Belum ada rekening.</TableCell></TableRow>}
            {banks.map(b => (
              <TableRow key={b.id} data-testid={`row-bank-${b.id}`}>
                <TableCell className="font-medium">{b.nama_bank}</TableCell>
                <TableCell className="font-mono">{b.nomor}</TableCell>
                <TableCell>{b.atas_nama}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={()=>setBankEdit(b)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={()=>delBank(b.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!bankEdit} onOpenChange={v=>!v && setBankEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{bankEdit?.id ? "Ubah Rekening" : "Tambah Rekening"}</DialogTitle></DialogHeader>
          {bankEdit && (<div className="space-y-3">
            <div><Label>Nama Bank</Label><Input value={bankEdit.nama_bank} onChange={e=>setBankEdit({...bankEdit,nama_bank:e.target.value})} data-testid="bank-nama" /></div>
            <div><Label>Nomor Rekening</Label><Input value={bankEdit.nomor} onChange={e=>setBankEdit({...bankEdit,nomor:e.target.value})} className="font-mono" data-testid="bank-nomor" /></div>
            <div><Label>Atas Nama</Label><Input value={bankEdit.atas_nama} onChange={e=>setBankEdit({...bankEdit,atas_nama:e.target.value})} data-testid="bank-atas-nama" /></div>
          </div>)}
          <DialogFooter><Button variant="outline" onClick={()=>setBankEdit(null)}>Batal</Button><Button onClick={saveBank} className="bg-brand text-white" data-testid="save-bank"><Save className="mr-1 h-4 w-4" /> Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
